import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal, SectionHead } from "@/lib/motion";
import { SERVICES } from "@/data/content";

export default function TaxServices() {
  return (
    <div data-testid="tax-services-page" className="pt-20">
      <section className="border-b border-steel-border">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <SectionHead
            overline="Tax Services"
            title="Individual tax returns — every shape they come in."
            copy="Each engagement follows the same discipline: review everything, ask questions, prepare carefully, and walk you through the result before anything is filed."
          />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10">
        {SERVICES.map((s, i) => {
          const pricingLink = s.priceItems && s.priceItems.length
            ? `/pricing?items=${s.priceItems.join(",")}`
            : "/pricing";
          return (
            <div key={s.id} id={s.id} data-testid={`service-detail-${s.id}`} className="scroll-mt-28 border-b border-steel-border/70 py-16 lg:py-20">
              <Reveal>
                <Link
                  to={pricingLink}
                  data-testid={`service-pricing-link-${s.id}`}
                  className="group grid grid-cols-1 gap-10 lg:grid-cols-12"
                >
                  <div className="lg:col-span-4">
                    <span className="font-mono text-sm text-steel-brass">{String(i + 1).padStart(2, "0")}</span>
                    <h2 className="mt-3 font-display text-3xl font-medium tracking-tight text-steel-text transition-colors duration-300 group-hover:text-steel-brass sm:text-4xl">
                      {s.title}
                    </h2>
                    <span className="mt-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-steel-muted transition-colors duration-300 group-hover:text-steel-brass">
                      See pricing
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                  <div className="lg:col-span-5">
                    <p className="text-base leading-relaxed text-steel-secondary sm:text-lg">{s.desc}</p>
                  </div>
                  <div className="lg:col-span-3">
                    <ul className="space-y-3">
                      {s.points.map((p) => (
                        <li key={p} className="flex items-start gap-3 text-sm text-steel-secondary">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-steel-brass" />
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Link>
              </Reveal>
            </div>
          );
        })}
      </section>

      <section className="bg-steel-surface">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-10 px-6 py-24 lg:flex-row lg:items-center lg:px-10">
          <Reveal>
            <h2 className="max-w-2xl font-display text-3xl font-medium leading-tight text-steel-text sm:text-4xl">
              Recognize your situation? Let&apos;s get it handled.
            </h2>
            <p className="mt-4 max-w-xl text-base text-steel-secondary">
              Start with a few questions about your situation, or run the Complexity Check first for an
              instant read on what your return involves.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-col gap-4 sm:flex-row">
              <Link
                to="/get-started"
                data-testid="services-start-btn"
                className="group flex items-center justify-center gap-2 bg-steel-accent px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-steel-accentHover"
              >
                Start My Tax Return
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/complexity"
                data-testid="services-complexity-btn"
                className="flex items-center justify-center gap-2 border border-steel-border px-8 py-4 text-sm font-medium text-steel-text transition-colors duration-300 hover:bg-steel-bg"
              >
                Complexity Check
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
